import type { CourseTreeNode } from "@/types/generated";
import { useNavigate, useLocation } from "react-router-dom";
import { getRegularSubchapters } from "@/lib/courseFinalQuiz";
import SubChapterNode from "@/components/course/tree/SubChapterNode";
import SectionNode from "@/components/course/tree/SectionNode";
import { ProgressRing } from "@/components/ui/progress-ring";
import { ChevronDown } from "lucide-react";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useCourseBasePath } from "@/hooks/useCourseBasePath";
import { useHideCourseProgress } from "@/hooks/useHideCourseProgress";
import type { AggregatedProgress } from "@/components/course/tree/types";
import {
  treeChromeClass,
  treeTitleClass,
} from "@/components/course/tree/treeStyles";
import { stripCourseNavQueryString } from "@/lib/chapterNavigation";
import { cn } from "@/lib/utils";

interface ChapterNodeProps {
  chapter: CourseTreeNode;
  chapterProgress?: AggregatedProgress;
}

const ChapterNode = ({ chapter, chapterProgress }: ChapterNodeProps) => {
  const { expandedChapters, toggleChapter, selectedNodeId, nodeIndex } =
    useCourseDetailStore();
  const parsedChapter = nodeIndex.get(chapter.id)?.node;
  const chapterIntro = parsedChapter?.intro;
  const chapterOutro = parsedChapter?.outro;
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = useCourseBasePath();
  const hideProgress = useHideCourseProgress();

  const isExpanded = expandedChapters.has(chapter.id);
  const subchapters = getRegularSubchapters(chapter, nodeIndex);

  const hasSubchapters = subchapters.length > 0;
  const hasIntro = Boolean(chapterIntro?.trim());
  const hasOutro = Boolean(chapterOutro?.trim());
  const isExpandable = hasSubchapters || hasIntro || hasOutro;
  const progress = chapterProgress?.progressPercent ?? 0;
  const isComplete = progress === 100;
  const isSelected = selectedNodeId === chapter.id;

  return (
    <div id={`node-${chapter.id}`}>
      {/* Chapter header */}
      <button
        onClick={() => {
          if (isExpandable) toggleChapter(chapter.id);
          if (selectedNodeId !== chapter.id) {
            navigate(
              `${basePath}/${chapter.id}${stripCourseNavQueryString(location.search)}`
            );
          }
        }}
        className="sb-chapter-header w-full flex items-center gap-2.5 pl-9 pr-5 py-2 text-left cursor-pointer hover:bg-gray-50 transition-colors relative"
        title={chapter.title}
      >
        {!hideProgress && (
          <ProgressRing
            percentage={progress}
            size="xs"
            fillColor={isComplete ? "var(--success)" : "var(--secondary)"}
          />
        )}
        <div className="flex-1 min-w-0">
          <span
            className={cn(
              "text-[16px] font-semibold block wrap-break-word leading-snug",
              treeTitleClass(isSelected)
            )}
          >
            {chapter.title}
          </span>
        </div>
        {isExpandable && (
          <ChevronDown
            className={cn(
              "w-4 h-4 shrink-0 transition-transform duration-200",
              treeChromeClass,
              isExpanded && "rotate-180"
            )}
          />
        )}
      </button>

      {/* Lessons */}
      {isExpanded && isExpandable && (
        <div className="sb-lessons relative ml-[44px] border-l border-gray-200">
          {hasIntro && <SectionNode parent={chapter} kind="intro" />}
          {subchapters.map((subchapter) => (
            <SubChapterNode key={subchapter.id} subchapter={subchapter} />
          ))}
          {hasOutro && <SectionNode parent={chapter} kind="outro" />}
        </div>
      )}
    </div>
  );
};

export default ChapterNode;
